import produce from 'immer'
import { keyBy, size } from 'lodash'

import { ALL } from '../../constants'
import { ISearchConfiguration } from '../../types'
import * as actionTypes from './action-types'
import * as hooks from './hooks'
import { IAction, IPayload, IState } from './types'

export const INITIAL_STATE: IState = {
  showFocusDropdown: false,
  showResults: false,
  showTabs: false,
  searchTerm: '',
  resultType: ALL,
  resultTypeIndex: 0,
  resultsByType: {},
  results: [],
  placeholderOptions: [],
  loading: false,
  tabLabels: [],
  error: null,
  allResultsLimits: {},
  configs: [],
  configsByLabel: {},
  allResultsOrder: [],
}

/**
 * getConfigUpdates: to update the configs in state when the configs prop changes
 */
const getConfigUpdates = (payload: IPayload, draft: IState) => {
  draft.configs = payload.configs.filter((c: ISearchConfiguration) => !c.hidden)
  draft.configsByLabel = keyBy(payload.configs, 'label')
}

/**
 * initialize: to initialize the state when the component mounts
 * Configs stuff is set up using getConfigUpdates though
 */
const initialize = (state: IState, { payload }: IAction) => {
  return produce(state, (draft: IState) => {
    draft.allResultsOrder = payload.allResultsOrder
    draft.allResultsLimits = payload.allResultsLimits
    draft.placeholderOptions = payload.placeholderOptions
    draft.tabLabels = payload.tabLabels
    draft.showTabs = size(payload.tabLabels) > 2
    getConfigUpdates(payload, draft)
  })
}

/**
 * updateConfigs: to update the configs in state when the configs prop changes
 */
const updateConfigs = (state: IState, { payload }: IAction) => {
  return produce(state, (draft: IState) => {
    getConfigUpdates(payload, draft)
  })
}

/**
 * focusInput: to show the focus dropdown when the input is focused and no search term is present
 * but show the results when there is a search term
 */
const focusInput = (state: IState) => {
  return produce(state, (draft: IState) => {
    draft.showFocusDropdown = state.searchTerm.length === 0
    draft.showResults = state.searchTerm.length > 0
  })
}

/**
 * blurInput: to hide the focus dropdown when the input is blurred
 * note: there are other fns that hide the dropdown, but this fn is called when the input is blurred
 */
const blurInput = (state: IState) => {
  return produce(state, (draft: IState) => {
    draft.showFocusDropdown = false
  })
}

/**
 * clearInput: to clear the input and hide the results
 * this is called when the "clear" x button is clicked
 */
const clearInput = (state: IState) => {
  return produce(state, (draft: IState) => {
    draft.showResults = false
    draft.showFocusDropdown = false
    draft.searchTerm = ''
    draft.results = []
  })
}

/**
 * selectResult: update state when selecting a result and hide the results
 */
const selectResult = (state: IState, { payload }: IAction) => {
  return produce(state, (draft: IState) => {
    draft.showResults = false
    draft.showFocusDropdown = false
    draft.searchTerm = payload.label
  })
}

/**
 * searchBegin: update state when the user starts typing
 */
const searchBegin = (state: IState, { payload }: IAction) => {
  return produce(state, (draft: IState) => {
    draft.loading = true
    draft.error = null
    draft.searchTerm = payload.searchTerm
    if (!payload.searchTerm) {
      draft.results = []
      draft.showResults = false
      draft.showFocusDropdown = true
    } else {
      draft.showResults = true
      draft.showFocusDropdown = false
    }
  })
}

/**
 * searchSuccess: update state when the search is successful
 * this is called when the promise returned by onSearch resolves
 */
const searchSuccess = (state: IState, { payload }: IAction) => {
  if (payload.searchTerm !== state.searchTerm) {
    // Guard against a stale response for a previous search term
    return state
  }
  return produce(state, (draft: IState) => {
    draft.error = null
    draft.loading = false
    draft.resultsByType = payload.resultsByType
    draft.results = payload.resultsByType[state.resultType]
  })
}

/**
 * searchFailure: update state when the search fails
 * this is called when the promise returned by onSearch rejects
 */
const searchFailure = (state: IState, { payload }: IAction) => {
  if (payload.searchTerm !== state.searchTerm) {
    return state
  }
  return produce(state, (draft: IState) => {
    draft.loading = false
    draft.error = payload.error
  })
}

/**
 * setResultType: update state when the user clicks on a tab
 */
const setResultType = (state: IState, { payload }: IAction) => {
  return produce(state, (draft: IState) => {
    const resultType = payload.resultType
    draft.resultType = resultType
    draft.results = state.resultsByType[resultType]
    draft.resultTypeIndex = draft.tabLabels.indexOf(resultType)
  })
}

/**
 * closeResults: to close the results when the user clicks outside the input
 */
const closeResults = (state: IState) => {
  return produce(state, (draft: IState) => {
    draft.showResults = false
    draft.showFocusDropdown = false
  })
}

/**
 * reducer: the reducer function
 */
export const reducer = (state = INITIAL_STATE, action: IAction) => {
  switch (action.type) {
    case actionTypes.INITIALIZE:
      return initialize(state, action)

    case actionTypes.FOCUS_INPUT:
      return focusInput(state)

    case actionTypes.BLUR_INPUT:
      return blurInput(state)

    case actionTypes.CLEAR_INPUT:
      return clearInput(state)

    case actionTypes.SEARCH_BEGIN:
      return searchBegin(state, action)

    case actionTypes.SEARCH_SUCCESS:
      return searchSuccess(state, action)

    case actionTypes.SEARCH_FAILURE:
      return searchFailure(state, action)

    case actionTypes.SET_RESULT_TYPE:
      return setResultType(state, action)

    case actionTypes.CLOSE_RESULTS:
      return closeResults(state)

    case actionTypes.UPDATE_CONFIGS:
      return updateConfigs(state, action)

    case actionTypes.SELECT_RESULT:
      return selectResult(state, action)

    default:
      return state
  }
}

export { actionTypes, hooks }
